/**
 * バッチ実行設定の一元管理
 *
 * 注意: この値はバックエンドの BATCH_MAX_CONCURRENCY 環境変数と一致させる必要があります
 * バックエンドのデフォルト値: 5 (ai-hack-backend/.env.example)
 *
 * 変更する場合は以下の両方を更新してください:
 * - フロントエンド: このファイルの MAX_CONCURRENT_BATCHES
 * - バックエンド: BATCH_MAX_CONCURRENCY 環境変数
 */

/**
 * 同時実行可能な最大バッチ数
 *
 * バックエンドの BATCH_MAX_CONCURRENCY と同じ値に設定する必要があります。
 * デフォルト: 5
 */
export const MAX_CONCURRENT_BATCHES =
  parseInt(import.meta.env.VITE_BATCH_MAX_CONCURRENCY || '') || 5

/**
 * バッチ実行に関する設定
 */
export const BATCH_CONFIG = {
  /** 同時実行可能な最大バッチ数 */
  maxConcurrentBatches: MAX_CONCURRENT_BATCHES,

  /**
   * 開始APIの応答待ちの上限（ミリ秒）
   *
   * D4：実測で5秒超が 22.5%・30秒超が 5.5% であり、30秒にすることで
   * 「遅いが正常に受理される」実行の大半を確認フェーズに回さずに受け切る。
   * この上限を超えても成功として扱わない（D2）。
   */
  startRequestTimeoutMs: 30 * 1000,

  /**
   * 確認フェーズ（D3）
   *
   * 開始APIの応答が得られなかった場合に、実行記録の有無で開始の有無を確定させる。
   * 1回目は即時、以降は間隔ごとに最大 maxAttempts 回（0 / 5 / 10 / 15 / 20秒）。
   * 各回は attemptTimeoutMs で打ち切り、前の回の結果を待たずに次を打つ。
   * 全体が deadlineMs に達したら未完了の問い合わせを待たずに打ち切る。
   */
  /**
   * 「時間がかかっています」を追加表示する閾値（ミリ秒／D5）
   *
   * 要件が許すのは 10〜30秒。実測 p50 = 2.67秒・p75 = 4.56秒であり、
   * 12秒であれば通常の実行では表示されない（FR-3-4）。
   * 確認フェーズに入る実行は必ず30秒を超えるため、表示は確実に出る（FR-3-5）。
   */
  slowNoticeThresholdMs: 12 * 1000,

  startConfirmationIntervalMs: 5 * 1000,
  startConfirmationMaxAttempts: 5,
  startConfirmationAttemptTimeoutMs: 5 * 1000,
  startConfirmationDeadlineMs: 30 * 1000,

  /** 実行中バッチの一括進捗ポーリング間隔（ミリ秒） */
  progressBulkPollingIntervalMs: 60 * 1000,

  /** 一括進捗レスポンスから一時的に欠落したバッチを保持する猶予時間（ミリ秒） */
  bulkResponseMissingGracePeriodMs: 60 * 1000,

  /** 分析履歴ポーリング間隔（ミリ秒） */
  analysisHistoryPollingIntervalMs: 30 * 1000,

  /** 実行履歴ポーリング間隔（ミリ秒） */
  executionHistoryPollingIntervalMs: 30 * 1000,

  /** 失敗したバッチの自動削除タイミング（ミリ秒） */
  failedBatchAutoDeleteDelay: 1000,

  /** localStorage からの復元対象期間（ミリ秒） */
  localStorageRetentionPeriod: 60 * 60 * 1000, // 1時間

  /** バッチ状態チェック間隔（ミリ秒） */
  batchStateCheckInterval: 60 * 1000, // 1分

  /** 一括進捗ポーリングの連続失敗上限（この回数に達するとAPI呼び出しを停止） */
  maxConsecutivePollFailures: 3,
} as const
